import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, type UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {type AxiosError} from 'axios'

import {riskSurveyMutations} from '../../api'
import type {CommentDto, CreateCommentRequest} from '../../types'
import {useRiskSurveyTranslation} from '../useRiskSurveyTranslation'

import {riskSurveyQueryKeys} from './riskSurveyQueryKeys'

type Variables = {
  riaId: string
  data: CreateCommentRequest
}

export const useAddRiaComment = (
  options?: UseMutationOptions<CommentDto, AxiosError, Variables>
) => {
  const {t} = useRiskSurveyTranslation()
  const queryClient = useQueryClient()

  return useMutation<CommentDto, AxiosError, Variables>({
    mutationFn: ({riaId, data}) => riskSurveyMutations.addComment(riaId, data),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      spawnSuccessToast(t('comments.addSuccess'))
      void options?.onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      spawnErrorToast(t('comments.addError'))
      void options?.onError?.(error, variables, onMutateResult, context)
    },
    onSettled: (data, error, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: riskSurveyQueryKeys.rias.comments(variables.riaId)
      })
      void queryClient.invalidateQueries({queryKey: riskSurveyQueryKeys.rias.lists()})
      void options?.onSettled?.(data, error, variables, onMutateResult, context)
    }
  })
}
